/**
 * Full chat panel for the AI Coach — header, scrolling message list,
 * empty state with quick-reply chips, "Send to Coach D" escalation
 * button, and composer.
 *
 * Pure presentation — all state lives in `useAICoach`.
 */
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Loader2, X, Send, Bot, RefreshCw, MessageSquarePlus, Image as ImageIcon } from 'lucide-react';
import { CoachMessageBubble } from './CoachMessageBubble';

const QUICK_REPLIES = [
  'How do I log a workout?',
  'Swap a meal in my plan',
  'Best move for upper chest?',
];

export const CoachChatPanel = ({ coach, onClose }) => {
  const {
    user, messages, input, setInput, sending, loadingHistory, escalating,
    inputRef, lastEscalateMsgId, sendMessage, escalate, clearHistory,
    attachedImage, uploadingImage, attachImage, removeAttachedImage,
  } = coach;

  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  // Auto-scroll to newest message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  return createPortal(
    <motion.div
      key="ai-coach-panel"
      initial={{ opacity: 0, y: 24, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 24, scale: 0.96 }}
      transition={{ type: 'spring', stiffness: 320, damping: 28 }}
      className="fixed bottom-16 right-4 z-[1250] w-[min(380px,calc(100vw-2rem))] h-[min(560px,calc(100vh-6rem))]
        flex flex-col rounded-2xl overflow-hidden
        bg-[#0b0b0e]/95 backdrop-blur-xl border border-yellow-300/25
        shadow-[0_22px_60px_rgba(0,0,0,0.7),inset_0_1px_0_rgba(255,255,255,0.05)]"
      data-testid="ai-coach-panel"
    >
      {/* Header */}
      <div className="relative px-4 py-3 flex items-center gap-3 border-b border-white/10
        bg-gradient-to-r from-yellow-300/15 via-primary/10 to-transparent">
        <div className="relative h-10 w-10 rounded-full bg-gradient-to-br from-yellow-300 to-primary
          flex items-center justify-center text-black shadow-[0_0_14px_rgba(212,175,55,0.55)]">
          <Bot className="h-5 w-5" strokeWidth={2.2} />
          <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-400 ring-2 ring-[#0b0b0e]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading uppercase tracking-[0.16em] text-sm text-white leading-tight">
            Coach D-zo
          </p>
          <p className="text-[10px] text-yellow-200/70 leading-tight">
            AI Coach · powered by Claude
          </p>
        </div>
        <button
          type="button"
          onClick={clearHistory}
          className="p-1.5 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Clear chat"
          data-testid="ai-coach-clear"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Close coach"
          data-testid="ai-coach-close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scroll-smooth"
        data-testid="ai-coach-messages"
      >
        {loadingHistory && (
          <div className="flex items-center justify-center py-6 text-white/40 text-sm">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
          </div>
        )}

        {!loadingHistory && messages.length === 0 && (
          <div className="py-6 text-center text-white/60 text-sm space-y-3">
            <p className="text-white/80 text-base font-medium">Hey {user.name?.split(' ')[0] || 'there'} 👋</p>
            <p className="leading-relaxed">
              I&apos;m Coach D-zo, your AI coach. Ask me about your program, swaps, form,
              nutrition, or how the app works. I&apos;ll loop the team in if something
              needs real eyes.
            </p>
            <div className="flex flex-wrap gap-2 justify-center pt-2">
              {QUICK_REPLIES.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => setInput(q)}
                  className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/80 text-xs hover:border-primary/50 hover:text-primary transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m) => (
          <CoachMessageBubble key={m.id} message={m} userName={user.name} />
        ))}

        {sending && (
          <div className="flex items-center gap-2 text-yellow-300/70 text-xs pl-1">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Coach is typing…
          </div>
        )}

        {lastEscalateMsgId && (
          <motion.button
            type="button"
            onClick={escalate}
            disabled={escalating}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full mt-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full
              bg-gradient-to-r from-sky-400 to-blue-700 text-white font-heading uppercase tracking-[0.16em] text-xs
              border border-sky-300/40 shadow-[0_6px_20px_rgba(56,189,248,0.4)]
              hover:shadow-[0_8px_28px_rgba(56,189,248,0.7)]
              disabled:opacity-60 transition-shadow"
            data-testid="ai-coach-escalate"
          >
            {escalating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <MessageSquarePlus className="h-4 w-4" />
            )}
            Send this to Coach D
          </motion.button>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-white/10 p-3 bg-black/40">
        {/* Attached image preview */}
        {attachedImage && (
          <div className="mb-2 relative inline-block" data-testid="ai-coach-image-preview">
            <img
              src={attachedImage.previewUrl}
              alt="Attached preview"
              className="h-20 w-20 object-cover rounded-xl border border-white/15"
            />
            <button
              type="button"
              onClick={removeAttachedImage}
              className="absolute -top-1.5 -right-1.5 h-6 w-6 rounded-full bg-black/90 border border-white/20 text-white/80 hover:text-white hover:border-red-500/50 flex items-center justify-center transition-colors"
              aria-label="Remove attached image"
              data-testid="ai-coach-image-remove"
            >
              <X className="h-3 w-3" />
            </button>
            {uploadingImage && (
              <div className="absolute inset-0 rounded-xl bg-black/70 flex items-center justify-center">
                <Loader2 className="h-5 w-5 text-primary animate-spin" />
              </div>
            )}
          </div>
        )}

        <div className="flex items-end gap-2">
          {/* Attach button */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending || uploadingImage}
            className="h-10 w-10 shrink-0 rounded-full flex items-center justify-center
              bg-white/[0.04] border border-white/15 text-white/70 hover:text-primary hover:border-primary/50
              disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Attach photo"
            data-testid="ai-coach-attach"
            title="Send a photo to the coach"
          >
            <ImageIcon className="h-4 w-4" strokeWidth={2} />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) attachImage(f);
              e.target.value = ''; // allow re-selecting the same file
            }}
            data-testid="ai-coach-file-input"
          />

          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder={attachedImage ? 'Add a note (optional)…' : 'Ask the coach…'}
            rows={1}
            maxLength={4000}
            className="flex-1 resize-none bg-black/60 border border-white/15 rounded-2xl px-3.5 py-2 text-sm text-white
              placeholder:text-white/40 focus:outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/30
              max-h-28"
            data-testid="ai-coach-input"
          />
          <button
            type="button"
            onClick={sendMessage}
            disabled={sending || uploadingImage || (!input.trim() && !attachedImage)}
            className="h-10 w-10 rounded-full flex items-center justify-center
              bg-gradient-to-br from-yellow-300 to-primary text-black
              border border-yellow-200/60 shadow-[0_6px_18px_rgba(212,175,55,0.45)]
              hover:shadow-[0_8px_26px_rgba(212,175,55,0.7)]
              disabled:opacity-50 disabled:cursor-not-allowed transition-shadow active:scale-95"
            data-testid="ai-coach-send"
            aria-label="Send message"
          >
            {sending || uploadingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" strokeWidth={2.2} />}
          </button>
        </div>
        <p className="text-[10px] text-white/30 mt-1.5 px-1">
          AI replies may not be perfect — verify anything safety-critical.
        </p>
      </div>
    </motion.div>,
    document.body
  );
};

export default CoachChatPanel;
